import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiJson } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "wouter";
import { format } from "date-fns";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";

interface FavouriteImageItem {
  id: number;
  evidenceImageId: number;
  postingEventId: number;
  storeId: number;
  storeName: string;
  operatorName: string;
  approvedByName: string;
  capturedAt: string;
  approvedAt: string;
  assetType: string;
  secureUrl: string | null;
}

interface FavouriteImagesResponse {
  success: boolean;
  data: {
    items: FavouriteImageItem[];
    pagination: {
      limit: number;
      nextCursor: string | null;
      hasMore: boolean;
    };
  };
}

export function ApprovedInstallerImagesTab({ campaignId, cycleId }: { campaignId: number; cycleId?: number | null }) {
  // When viewing from campaign detail page, show all favorited images for the campaign
  // No need for cycle/store/operator filters - campaign context is already established
  const [items, setItems] = useState<FavouriteImageItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FavouriteImageItem | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const buildUrl = (cursor?: string | null) => {
    const params = new URLSearchParams();
    // Don't filter by cycle/store/operator when viewing campaign details
    // Show all favorited images for this campaign
    if (cursor) params.set("cursor", cursor);
    params.set("limit", "30");
    return `/api/campaigns/${campaignId}/favourite-images?${params.toString()}`;
  };

  const { data, isLoading, error, refetch } = useQuery<FavouriteImagesResponse>({
    queryKey: ["campaign-favourite-images", campaignId],
    queryFn: async () => apiJson<FavouriteImagesResponse>(buildUrl()),
    enabled: !!campaignId,
  });

  useEffect(() => {
    if (data?.success) {
      setItems(data.data.items || []);
      setNextCursor(data.data.pagination.nextCursor || null);
    }
  }, [data]);

  useEffect(() => {
    setItems([]);
    setNextCursor(null);
  }, [campaignId]);

  const handleLoadMore = async () => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const next = await apiJson<FavouriteImagesResponse>(buildUrl(nextCursor));
      if (next?.success) {
        setItems((prev) => [...prev, ...(next.data.items || [])]);
        setNextCursor(next.data.pagination.nextCursor || null);
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <Card>
      <CardHeader
        className="flex flex-row items-center justify-between space-y-0 cursor-pointer select-none"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <CardTitle>Retailer Ready Images</CardTitle>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" type="button">
          {isCollapsed ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </CardHeader>
      {!isCollapsed && (
        <CardContent className="space-y-4">
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading retailer ready images…
          </div>
        ) : error ? (
          <div className="text-sm text-destructive">Failed to load retailer ready images.</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground">No retailer ready images yet.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedItem(item)}
                  className="group relative text-left bg-card border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-square w-full overflow-hidden bg-muted">
                    <img
                      src={item.secureUrl || ""}
                      alt="Retailer ready"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  </div>
                  <div className="p-3 space-y-1.5">
                    <div className="text-sm font-semibold text-foreground line-clamp-1">
                      {item.storeName}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {item.operatorName}
                    </div>
                  <div className="text-xs text-muted-foreground">
                    Approved by {item.approvedByName || 'Unknown'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {format(new Date(item.approvedAt), "MMM d, yyyy")}
                  </div>
                  </div>
                </button>
              ))}
            </div>

            {nextCursor && (
              <div className="flex justify-center">
                <Button variant="outline" onClick={handleLoadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Loading…
                    </>
                  ) : (
                    "Load more"
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
      )}

      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Retailer Ready Image</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <img
                src={selectedItem.secureUrl || ""}
                alt="Retailer ready"
                className="w-full max-h-[60vh] object-contain rounded-md border"
              />
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-medium text-muted-foreground mb-1">Store</div>
                    <div className="font-medium">{selectedItem.storeName}</div>
                  </div>
                  <div>
                    <div className="font-medium text-muted-foreground mb-1">Operator</div>
                    <div className="font-medium">{selectedItem.operatorName}</div>
                  </div>
                  <div>
                    <div className="font-medium text-muted-foreground mb-1">Submitted</div>
                    <div className="font-medium">
                      {format(new Date(selectedItem.capturedAt), "MMM d, yyyy h:mm a")}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-muted-foreground mb-1">Approved By</div>
                    <div className="font-medium">{selectedItem.approvedByName || 'Unknown'}</div>
                  </div>
                  <div>
                    <div className="font-medium text-muted-foreground mb-1">Approved On</div>
                    <div className="font-medium">
                      {format(new Date(selectedItem.approvedAt), "MMM d, yyyy h:mm a")}
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                <Link href={`/admin/activation-cockpit/review?submissionId=${selectedItem.postingEventId}`}>
                  View original submission (Event #{selectedItem.postingEventId})
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
